import { useAuthStore } from '@/stores/authStore'
import { useBranchStore } from '@/stores/branchStore'
import { createRouter, createWebHashHistory } from 'vue-router'
import ErrorView from '../views/ErrorView.vue'

const router = createRouter({
  history: createWebHashHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.name === 'products') return false
    return { top: 0 }
  },
  routes: [
    {
      path: '/:branchCode/home',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
    },
    {
      path: '/:branchCode/products',
      name: 'products',
      component: () => import('../views/ProductsView.vue'),
    },
    {
      path: '/:branchCode/products/:id',
      name: 'product-detail',
      component: () => import('../views/ProductDetailView.vue'),
      props: true,
    },
    {
      path: '/:branchCode/cart',
      name: 'cart',
      component: () => import('../views/CartView.vue'),
    },
    {
      path: '/:branchCode/orders',
      name: 'orders',
      component: () => import('../views/OrderListView.vue'),
    },
    {
      path: '/:branchCode/orders/:id/edit',
      name: 'order-edit',
      component: () => import('../views/OrderEditView.vue'),
    },
    {
      path: '/consent',
      name: 'consent-form',
      component: () => import('../views/ConsentFormView.vue'),
      props: (route) => ({ edit: route.query.edit === 'true' }),
      meta: {
        title: 'カラコンWeb同意書 | ホテラバ',
        branchAgnostic: true,
      },
    },
    {
      path: '/consent/complete',
      name: 'consent-complete',
      component: () => import('../views/ConsentCompleteView.vue'),
      meta: {
        title: 'カラコンWeb同意書 | ホテラバ',
        branchAgnostic: true,
      },
    },
    {
      path: '/error',
      name: 'error',
      component: ErrorView,
      meta: {
        title: 'エラー | ホテラバ',
      },
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const branchStore = useBranchStore()

  // error route: auth も branch も不要、素通し
  if (to.name === 'error') {
    next()
    return
  }

  // A. 完全不明ルート → default branch 配下に置き換えて救う
  //    routes は全て /:branchCode/{sub} (2セグメント以上) なので、旧QR (/products, /products/:id, /cart 等) と /
  //    は全てここに落ちる。
  if (to.matched.length === 0) {
    await ensureBranches(branchStore)
    const defaultBranch = branchStore.defaultBranch
    if (!defaultBranch) {
      next({ name: 'error' })
      return
    }
    const targetPath = `/${defaultBranch.code}${to.path === '/' ? '/home' : to.path}`
    const resolved = router.resolve({ path: targetPath, query: to.query })
    next(
      resolved.matched.length > 0
        ? { path: targetPath, query: to.query }
        : { name: 'home', params: { branchCode: defaultBranch.code } },
    )
    return
  }

  // B. auth 担保（回復可能な箇所のみ try/catch）
  if (!authStore.is_anonymous_loggedin) {
    try {
      await authStore.authed_hello_anonymous()
    } catch {
      try {
        await authStore.signup_anonymous()
      } catch {
        next({ name: 'error' })
        return
      }
    }
  }

  // C. branchAgnostic (consent系) → 通す
  if (to.meta.branchAgnostic) {
    next()
    return
  }

  // D. branches確保 & branchCode解決
  await ensureBranches(branchStore)
  const branchCode = to.params.branchCode as string | undefined
  if (branchCode && branchStore.existsCurrentBranch(branchCode)) {
    next()
    return
  }

  // E. 無効 branchCode → console準拠: name保持 + branchCodeだけ差し替え
  const defaultBranch = branchStore.defaultBranch
  if (!defaultBranch) {
    next({ name: 'error' })
    return
  }
  next({
    name: (to.name || 'home') as string,
    params: { ...to.params, branchCode: defaultBranch.code },
    query: to.query,
  })
})

async function ensureBranches(branchStore: ReturnType<typeof useBranchStore>) {
  if (branchStore.branches.length > 0) return
  try {
    await branchStore.getBranches()
  } catch {
    // エラーはbranchStore内でハンドリング済み
  }
}

router.afterEach((to) => {
  const branchStore = useBranchStore()
  const defaultTitle = branchStore.currentBranch?.name ?? 'ホテラバ'
  document.title = (to.meta.title as string) || defaultTitle
})

// チャンク読み込み失敗（デプロイ跨ぎ・一時的な回線断）はリロードで復旧する。
// 復旧しない場合の無限ループを避けるため、リロードは1回のみ。
// リロードでも復旧しなければ /error（ErrorView は静的 import なので chunk 取得不要）へ退避する。
// 退避時にフラグを解除するのは、同一タブが後続デプロイで復旧できる余地を残すため
// （sessionStorage はリロードを跨いで残る。解除してもユーザー操作を挟まないと再リロードしないためループにはならない）。
const DYNAMIC_RELOAD_KEY = 'dynamic-import-reload'
router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes("Couldn't resolve component")
  ) {
    if (!sessionStorage.getItem(DYNAMIC_RELOAD_KEY)) {
      sessionStorage.setItem(DYNAMIC_RELOAD_KEY, '1')
      window.location.hash = to.fullPath
      window.location.reload()
    } else {
      sessionStorage.removeItem(DYNAMIC_RELOAD_KEY)
      router.replace({ name: 'error' })
    }
  }
})
router
  .isReady()
  .then(() => sessionStorage.removeItem(DYNAMIC_RELOAD_KEY))
  .catch(() => {}) // 初回 navigation 失敗時は onError 側で /error へ退避済み。unhandled rejection を防ぐ

export default router
